import DeleteIcon from '@mui/icons-material/Delete';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import {
	Box,
	Chip,
	Grid,
	IconButton,
	Link as MaterialLink,
	Theme,
	Tooltip,
	Typography,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { AuthData, HIDE_PERIL_LIMITS_ENABLE } from '../../../context/auth';
import {
	EndorsementData,
	withEndorsementContext,
} from '../../../context/EndorsementProvider';
import { ErrorData } from '../../../context/ErrorProvider';
import { Exposure } from '../../../types/policy';
import { FastForm } from '../../fastForm';
import { errorToast, successToast } from '../../Toast';
import ConfirmationModal from '../ConfirmationModal';
import { getPerilName } from '../util_sharedEndorsementAndRenewal';
import AddPerilModal from './AddEndorsementPerilModal';
import { isEndorsementPerilActive } from './util';

interface Props {
	authData: AuthData;
	userHasWriteAccess?: boolean;
	endorsementData: EndorsementData;
	errorData: ErrorData;
}

const useStyles = makeStyles((theme: Theme) => ({
	titleWrapper: {
		display: 'flex',
		alignItems: 'flex-end',
		marginRight: 'auto',
	},
	title: {
		textTransform: 'capitalize',
		flexGrow: 1,
		fontSize: '18px',
	},
	auxDataLink: {
		marginLeft: theme.spacing(1),
		marginRight: theme.spacing(1),
	},
	removeIcon: {
		position: 'relative',
		top: theme.spacing(1),
	},
}));

/**
 * Peril detail page for existing perils on existing exposures on endorsements,
 * The perilId in the url will be the peril's displayId.
 */
const ExistingPerilDetails = (props: Props) => {
	const classes = useStyles();
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { getValues, formState, reset } = useFormContext();
	const { dirtyFields } = formState;
	const params = useParams();
	const { userHasWriteAccess, endorsementData, authData } = props;

	const {
		endorsementUpdateObj,
		productConfig,
		setLoading,
		fields,
		saveFastForm,
	} = endorsementData;
	const selectedExposure = endorsementData.selectedExposure as Exposure;

	const { state } = endorsementUpdateObj;

	const { perilId } = params;

	const [isConfirmModalOpen, setIsConfirmModalOpen] = React.useState(false);

	const selectedPeril = selectedExposure.perils.find(
		(peril) => peril.displayId === perilId,
	);

	if (!selectedPeril || !selectedPeril.characteristics) {
		return null;
	}

	const policyConfig = productConfig.policyConfiguration;
	const exposureConfig = policyConfig.exposures.find(
		(exposure) => exposure.name === selectedExposure.name,
	);
	const perilConfig = exposureConfig.perils.find(
		(p) => p.name === selectedPeril.name,
	);
	const perilConfigs = exposureConfig.perils;
	const displayName = getPerilName(
		selectedPeril,
		perilConfig.displayName,
		selectedExposure,
		null,
		endorsementUpdateObj,
	);
	const isInEditMode =
		authData.hasPermission('endorsement.update') &&
		endorsementUpdateObj.state === 'application';

	const isEditable =
		authData.hasPermission('endorsement.update') && state === 'application';
	const isActive = isEndorsementPerilActive(
		selectedPeril,
		endorsementData.policy,
		endorsementUpdateObj,
	);

	const hidePerilLimitsEnabled =
		authData?.auth?.configProperties?.[HIDE_PERIL_LIMITS_ENABLE] ?? false;

	const removePeril = async () => {
		try {
			setLoading(true);
			setIsConfirmModalOpen(false);
			await saveFastForm(getValues(), dirtyFields, reset, {
				removePeril: {
					perilLocator: selectedPeril.locator,
					exposureLocator: selectedExposure.locator,
				},
			});
			navigate(
				`/policy/${params.policyId}/endorsements/${params.endorsementId}/exposures/${params.exposureId}/perils`,
			);
			successToast('Successfully removed peril');
		} catch (e) {
			errorToast('Error removing peril');
		} finally {
			setLoading(false);
		}
	};

	const renderRemovePeril = () => {
		return (
			<Tooltip title={'Remove ' + t('peril.singular.upper')}>
				<IconButton
					aria-label='Remove'
					sx={{ position: 'relative', top: '8px' }}
					onClick={() => setIsConfirmModalOpen(true)}
					className={classes.removeIcon}
				>
					<DeleteIcon
						sx={{
							'&:hover': {
								backgroundColor: '#f4433',
							},
						}}
					/>
				</IconButton>
			</Tooltip>
		);
	};

	const renderRemovePerilModal = () => {
		return (
			<ConfirmationModal
				entityName={selectedPeril.name}
				entityType={t('peril.singular.lower')}
				authToken={authData.auth.authorizationToken}
				onRemove={removePeril}
				cancel={() => setIsConfirmModalOpen(false)}
			/>
		);
	};

	const renderActions = () => {
		return (
			<AddPerilModal
				perilConfigs={perilConfigs}
				exposureLocator={selectedExposure.locator}
			/>
		);
	};

	if (!endorsementUpdateObj) {
		return null;
	}

	return (
		<>
			<Box className={classes.titleWrapper}>
				<Typography className={classes.title}>{displayName}</Typography>
				{authData.hasPermission('auxData.fetch') && (
					<MaterialLink
						className={classes.auxDataLink}
						component={RouterLink}
						to='auxData'
					>
						Aux Data
					</MaterialLink>
				)}
				{!isActive && (
					<Chip
						color='default'
						label='Inactive'
						icon={<ErrorOutlineRoundedIcon />}
					/>
				)}
				{isEditable && renderRemovePeril()}
				{isConfirmModalOpen && renderRemovePerilModal()}
			</Box>
			<hr />
			{!hidePerilLimitsEnabled ? <h4>Limits</h4> : null}
			<Grid container spacing={2}>
				<FastForm
					formName={selectedExposure.locator + '.' + selectedPeril.locator}
					form={fields}
					configVersion={productConfig.configVersion}
					authData={authData}
					productName={productConfig.name}
					disabled={!isInEditMode || userHasWriteAccess || !isActive}
					save={saveFastForm}
					setLoading={setLoading}
					effectiveDate={endorsementUpdateObj.startTimestamp}
					disableExternalCalls
				/>
			</Grid>
			{isInEditMode && renderActions()}
		</>
	);
};

export default withEndorsementContext<Props>(ExistingPerilDetails);
